import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axiosRestaurantInstance from '../restaurant/AxiosRestaurantInstance';
import { ORGANIZATION_API_PATHS } from '../constants/apiPaths';
import EatsAdminLayout from './EatsAdminLayout';
import EatsAdminPageHeader from './EatsAdminPageHeader';
import CollapsibleFilters from '../restaurant/CollapsibleFilters';
import { AdminNameCell, AdminRowActions } from './AdminRowCells';
import { confirmAction } from '../restaurant/ui/feedback';
import AddressSearchFields, {
  EMPTY_ADDRESS,
  addressFromApi,
  formatAddressLine,
  isAddressComplete,
} from './AddressSearchFields';
import {
  formatAadharDisplay,
  formatAadharDisplayOrFallback,
  formatMobileDisplayOrFallback,
  sanitizeAadharInput,
  validateAadharNumber,
} from '../restaurant/formatDisplay';
import { useAutoDismissMessage } from '../utils/useAutoDismissMessage';
import { isEatsAppRootAdmin } from '../utils/eatsAppAuth';
import './EatsAdminCreatePage.css';
import './EatsAdminDashboard.css';
import './AppUsersPage.css';

function normalizeUserRow(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw.id ?? raw.userId ?? raw.user_id ?? raw.User_Id;
  if (id == null) return null;
  const firstName = String(raw.firstName ?? raw.first_name ?? raw.First_Name ?? '').trim();
  const lastName = String(raw.lastName ?? raw.last_name ?? raw.Last_Name ?? '').trim();
  return {
    id: String(id),
    firstName,
    lastName,
    name: [firstName, lastName].filter(Boolean).join(', ') || raw.name || raw.fullName || '',
    email: raw.email ?? raw.Email ?? '',
    aadharNo: raw.aadharNo ?? raw.aadhar_no ?? raw.aadharNumber ?? '',
    mobile: raw.mobile ?? raw.mobileNumber ?? raw.contactNumber ?? raw.Contact_Number ?? '',
    role:
      raw.role ??
      raw.roleName ??
      (Array.isArray(raw.roles) && raw.roles.length > 0 ? raw.roles.join(', ') : ''),
    status: String(raw.status ?? raw.Status ?? 'ACTIVE').toUpperCase(),
    source:
      raw.sourceApp ??
      raw.app ??
      raw.application ??
      raw.platform ??
      raw.userType ??
      'EATS',
    address: raw.address && typeof raw.address === 'object' ? raw.address : null,
  };
}

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  contactNumber: '',
  aadharNumber: '',
  password: '',
  roleName: 'APP_USER',
  status: 'ACTIVE',
  address: { ...EMPTY_ADDRESS },
};

const FORM_TITLES = { add: 'Add Employee', view: 'Employee Details', edit: 'Edit Employee' };

const formFromUser = (u) => ({
  ...EMPTY_FORM,
  firstName: u.firstName || '',
  lastName: u.lastName || '',
  email: u.email || '',
  contactNumber: u.mobile || '',
  aadharNumber: sanitizeAadharInput(u.aadharNo || ''),
  roleName: u.role || 'APP_USER',
  status: u.status || 'ACTIVE',
  address: addressFromApi(u.address),
});

const addressPayload = (address) => ({
  addressLine1: address.addressLine1.trim(),
  addressLine2: String(address.addressLine2 || '').trim(),
  areaName: String(address.areaName || '').trim(),
  city: address.city.trim(),
  state: address.state.trim(),
  postalCode: String(address.postalCode || '').trim(),
  country: String(address.country || '').trim(),
  lat: address.lat === '' ? null : Number(address.lat),
  lon: address.lon === '' ? null : Number(address.lon),
});

const renderStatusBadge = (status) => {
  if (!status) return '-';
  const normalized = String(status).toUpperCase();
  const className =
    normalized === 'ACTIVE'
      ? 'eats-admin-status eats-admin-status--active'
      : normalized === 'INACTIVE'
        ? 'eats-admin-status eats-admin-status--inactive'
        : 'eats-admin-status eats-admin-status--default';
  return <span className={className} title={status}>{status}</span>;
};

const AppUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  useAutoDismissMessage(error, setError);
  useAutoDismissMessage(success, setSuccess);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [formMode, setFormMode] = useState(null);
  const [activeUser, setActiveUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const canAdd = isEatsAppRootAdmin();
  const canManage = canAdd;

  const closeAddForm = () => {
    setFormMode(null);
    setActiveUser(null);
    setForm(EMPTY_FORM);
    setError('');
  };

  const openUserForm = (mode, user) => {
    setError('');
    setActiveUser(user);
    setForm(formFromUser(user));
    setFormMode(mode);
  };

  const deactivateUser = async (user) => {
    const ok = await confirmAction({
      title: 'Deactivate employee?',
      message: `Deactivate ${user.name || 'this employee'}? They will no longer be able to sign in.`,
      confirmLabel: 'Deactivate',
      danger: true,
    });
    if (!ok) return;
    setError('');
    try {
      await axiosRestaurantInstance.delete(ORGANIZATION_API_PATHS.eatsBusinessUser(user.id));
      setSuccess('Employee deactivated.');
      setUsers((rows) => rows.map((r) => (r.id === user.id ? { ...r, status: 'INACTIVE' } : r)));
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to deactivate employee.');
    }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const res = await axiosRestaurantInstance.get(ORGANIZATION_API_PATHS.eatsBusinessUsers);
      const payload = res?.data;
      const rawRows = Array.isArray(payload)
        ? payload
        : payload?.users || payload?.data || payload?.items || [];
      const normalized = (Array.isArray(rawRows) ? rawRows : [])
        .map(normalizeUserRow)
        .filter(Boolean);
      setUsers(normalized);
    } catch (e) {
      const backendMsg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Unable to load employees.';
      setError(backendMsg);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const roleOptions = useMemo(() => {
    const set = new Set();
    users.forEach((u) => {
      if (u.role) set.add(String(u.role));
    });
    return ['ALL', ...Array.from(set).sort()];
  }, [users]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return users.filter((u) => {
      const roleMatch = roleFilter === 'ALL' || String(u.role) === roleFilter;
      if (!roleMatch) return false;
      if (!query) return true;
      return (
        String(u.id).toLowerCase().includes(query) ||
        String(u.name).toLowerCase().includes(query) ||
        String(u.firstName).toLowerCase().includes(query) ||
        String(u.lastName).toLowerCase().includes(query) ||
        String(u.email).toLowerCase().includes(query) ||
        String(u.aadharNo).toLowerCase().includes(query) ||
        String(u.mobile).toLowerCase().includes(query) ||
        String(u.source).toLowerCase().includes(query) ||
        formatAddressLine(u.address).toLowerCase().includes(query)
      );
    });
  }, [users, search, roleFilter]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    if (!firstName) {
      setError('First name is required.');
      return;
    }
    if (!lastName) {
      setError('Last name is required.');
      return;
    }

    const aadharErr = validateAadharNumber(form.aadharNumber);
    if (aadharErr) {
      setError(aadharErr);
      return;
    }
    if (!isAddressComplete(form.address)) {
      setError("Address is required. Search and select the employee's address.");
      return;
    }

    const isEdit = formMode === 'edit';
    setSaving(true);
    try {
      if (isEdit) {
        await axiosRestaurantInstance.put(ORGANIZATION_API_PATHS.eatsBusinessUser(activeUser.id), {
          firstName,
          lastName,
          email: form.email.trim(),
          mobile: form.contactNumber.trim(),
          aadharNumber: sanitizeAadharInput(form.aadharNumber),
          roleName: form.roleName,
          status: form.status,
          address: addressPayload(form.address),
        });
      } else {
        await axiosRestaurantInstance.post(ORGANIZATION_API_PATHS.eatsBusinessUsers, {
          firstName,
          lastName,
          name: `${firstName} ${lastName}`.trim(),
          email: form.email.trim(),
          contactNumber: form.contactNumber.trim(),
          aadharNumber: sanitizeAadharInput(form.aadharNumber),
          password: form.password,
          roleName: form.roleName,
          address: addressPayload(form.address),
        });
      }
      setSuccess(isEdit ? 'Employee updated successfully.' : 'Employee created successfully.');
      closeAddForm();
      await fetchUsers();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          (isEdit ? 'Unable to update employee.' : 'Unable to create employee.')
      );
    } finally {
      setSaving(false);
    }
  };

  if (formMode && (formMode === 'view' || canManage)) {
    const readOnly = formMode === 'view';
    const isAdd = formMode === 'add';
    const title = FORM_TITLES[formMode];
    return (
      <EatsAdminLayout activeTab="users">
        <div className="eats-admin-create-page">
          <EatsAdminPageHeader
            title={isAdd ? title : `${title}${activeUser?.name ? ` · ${activeUser.name}` : ''}`}
            subtitle={
              isAdd
                ? 'Create an application-level employee for Maya Eats Business.'
                : `Employee #${activeUser?.id}`
            }
            onBack={closeAddForm}
            actions={
              readOnly && canManage ? (
                <button
                  type="button"
                  className="eats-admin-btn eats-admin-btn--primary"
                  onClick={() => setFormMode('edit')}
                >
                  Edit
                </button>
              ) : null
            }
            beforeTitle={
              <nav className="eats-admin-breadcrumb" aria-label="Breadcrumb">
                <button type="button" className="eats-admin-breadcrumb__link" onClick={closeAddForm}>
                  Employees
                </button>
                <span className="eats-admin-breadcrumb__sep" aria-hidden>
                  /
                </span>
                <span className="eats-admin-breadcrumb__current">{title}</span>
              </nav>
            }
          />

          <div className="eats-admin-panel">
            {error ? <div className="eats-admin-error">{error}</div> : null}
            <form onSubmit={handleCreate}>
              <fieldset className="employees-fieldset" disabled={readOnly || saving}>
              <div className="eats-admin-form-grid employees-form-grid">
                <label className="employees-field">
                  <span>First name</span>
                  <input
                    className="eats-admin-input"
                    required
                    value={form.firstName}
                    onChange={(e) => updateForm('firstName', e.target.value)}
                    placeholder="First name"
                  />
                </label>
                <label className="employees-field">
                  <span>Last name</span>
                  <input
                    className="eats-admin-input"
                    required
                    value={form.lastName}
                    onChange={(e) => updateForm('lastName', e.target.value)}
                    placeholder="Last name"
                  />
                </label>
                <label className="employees-field">
                  <span>Email</span>
                  <input
                    className="eats-admin-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => updateForm('email', e.target.value)}
                    placeholder="name@company.com"
                  />
                </label>
                <label className="employees-field">
                  <span>Mobile</span>
                  <input
                    className="eats-admin-input"
                    required
                    value={form.contactNumber}
                    onChange={(e) => updateForm('contactNumber', e.target.value)}
                    placeholder="Mobile number"
                  />
                </label>
                <label className="employees-field">
                  <span>Aadhaar</span>
                  <input
                    className="eats-admin-input"
                    required
                    value={formatAadharDisplay(form.aadharNumber)}
                    onChange={(e) => updateForm('aadharNumber', sanitizeAadharInput(e.target.value))}
                    maxLength={14}
                    inputMode="numeric"
                    placeholder="XXXX XXXX XXXX"
                  />
                </label>
                {isAdd ? (
                  <label className="employees-field">
                    <span>Password</span>
                    <input
                      className="eats-admin-input"
                      required
                      type="password"
                      value={form.password}
                      onChange={(e) => updateForm('password', e.target.value)}
                      placeholder="Temporary password"
                    />
                  </label>
                ) : null}
                <label className="employees-field">
                  <span>Role</span>
                  <select
                    className="eats-admin-input"
                    value={form.roleName}
                    onChange={(e) => updateForm('roleName', e.target.value)}
                  >
                    <option value="APP_USER">APP_USER</option>
                    <option value="APP_ADMIN">APP_ADMIN</option>
                    {form.roleName === 'ROOT_ADMIN' ? <option value="ROOT_ADMIN">ROOT_ADMIN</option> : null}
                  </select>
                </label>
                {!isAdd ? (
                  <label className="employees-field">
                    <span>Status</span>
                    <select
                      className="eats-admin-input"
                      value={form.status}
                      onChange={(e) => updateForm('status', e.target.value)}
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  </label>
                ) : null}
                <AddressSearchFields
                  key={`${formMode}-${activeUser?.id || 'new'}`}
                  value={form.address}
                  onChange={(address) => updateForm('address', address)}
                  disabled={readOnly}
                />
              </div>
              </fieldset>
              <div className="eats-admin-actions">
                <button
                  type="button"
                  className="eats-admin-btn eats-admin-btn--secondary"
                  onClick={readOnly || isAdd ? closeAddForm : () => openUserForm('view', activeUser)}
                  disabled={saving}
                >
                  {readOnly ? 'Back' : 'Cancel'}
                </button>
                {readOnly ? null : (
                  <button type="submit" className="eats-admin-btn eats-admin-btn--primary" disabled={saving}>
                    {saving
                      ? isAdd
                        ? 'Creating...'
                        : 'Saving...'
                      : isAdd
                        ? 'Create employee'
                        : 'Save changes'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </EatsAdminLayout>
    );
  }

  return (
    <EatsAdminLayout activeTab="users">
      <h2 className="eats-admin-dashboard__title">Employees</h2>
      <p className="eats-admin-dashboard__subtitle">
        Application-level admins and employees across Maya Eats Business.
      </p>

      <div className="eats-admin-panel">
        <div className="eats-admin-search-bar eats-admin-search-bar--employees">
          <input
            className="eats-admin-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by id, name, email, aadhar, mobile..."
          />
          <CollapsibleFilters activeCount={roleFilter && roleFilter !== 'ALL' ? 1 : 0}>
            <select
              className="eats-admin-input eats-admin-role-filter"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              aria-label="Filter by role"
            >
              {roleOptions.map((role) => (
                <option key={role} value={role}>
                  {role === 'ALL' ? 'All roles' : role}
                </option>
              ))}
            </select>
          </CollapsibleFilters>
          <button
            type="button"
            className="eats-admin-btn eats-admin-btn--secondary"
            onClick={fetchUsers}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          {canAdd ? (
            <button
              type="button"
              className="eats-admin-btn eats-admin-btn--create"
              onClick={() => {
                setError('');
                setActiveUser(null);
                setForm(EMPTY_FORM);
                setFormMode('add');
              }}
            >
              Add Employee
            </button>
          ) : null}
        </div>

        {error ? <div className="eats-admin-error">{error}</div> : null}
        {success ? <div className="eats-admin-success">{success}</div> : null}

        <div className="eats-admin-table-wrap">
          <table className="eats-admin-table eats-admin-table--restaurants eats-admin-table--all-actions eats-admin-table--employees">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Source</th>
                <th>Mobile</th>
                <th>Aadhar No</th>
                <th className="eats-admin-col-status">Status</th>
                <th>Email</th>
                <th className="eats-admin-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="eats-admin-table__empty">
                    {loading ? 'Loading employees...' : 'No employees found.'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => {
                  const name = u.name || `${u.firstName} ${u.lastName}`.trim() || '-';
                  return (
                    <tr key={u.id}>
                      <td>
                        <AdminNameCell name={name} id={u.id} status={u.status} />
                      </td>
                      <td>{u.role || '-'}</td>
                      <td>{u.source || '-'}</td>
                      <td className="employees-mobile">{formatMobileDisplayOrFallback(u.mobile, '-')}</td>
                      <td>{formatAadharDisplayOrFallback(u.aadharNo, '-')}</td>
                      <td className="eats-admin-col-status">{renderStatusBadge(u.status)}</td>
                      <td className="eats-admin-cell-email">{u.email || '-'}</td>
                      <td className="eats-admin-col-actions">
                        <AdminRowActions
                          name={name}
                          phone={u.mobile}
                          email={u.email}
                          onView={() => openUserForm('view', u)}
                          onEdit={canManage ? () => openUserForm('edit', u) : null}
                          onDeactivate={
                            canManage && u.status === 'ACTIVE' ? () => deactivateUser(u) : null
                          }
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </EatsAdminLayout>
  );
};

export default AppUsersPage;
